import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import { JwtPayload, PermissionType } from '@companion-professional/dctypes';

// TODO: Maybe migrate this (and the useLogin hook) to it's own library?  I'm not sure what the final shape of this
//  will be, so for now I'm keeping this in the application that it's used in.

export type AuthState = {
  token: string | null;
  email: string | null;
  parsedToken: JwtPayload | null;
  setToken: (token: string) => void;
  setEmail: (email: string) => void;
  setParsedToken: (parsedToken: JwtPayload | null) => void;
  logout: () => void;

  // switchClinicId can be set to a clinic ID to indicate that the user is in the process of switching clinics.  This
  // is used to show the switch clinic dialog, where the user can confirm that they want to switch clinics.  If they
  // select "Yes", then the clinic will be switched to this value and the page will be refreshed.
  switchClinicId: string;
  setSwitchClinicId: (clinicId: string) => void;
};

export const useAuthStore = create(
  // devtools is a middleware that allows use to use the Redux devtools extension.  Note this is only available in
  // development mode.
  devtools(
    // persist the auth state to local storage.
    persist<AuthState>(
      (set) => ({
        token: null,
        parsedToken: null,
        email: null,
        setToken: (token) => {
          if (token && token.length) {
            set({ token, parsedToken: jwtDecode<JwtPayload>(token) });
          }
        },
        setEmail: (email) => {
          set({ email });
        },
        setParsedToken: (parsedToken) => {
          set({ parsedToken });
        },
        logout: () => {
          set({ token: null });
        },
        switchClinicId: '',
        setSwitchClinicId: (clinicId) => {
          set({ switchClinicId: clinicId });
        }
      }),
      {
        // This is the key that the state will be stored under in local storage.
        name: 'vportal-auth',

        // If we are in the password needs reset state, we don't want to persist the token to local storage.  That way,
        // the user will see the login page upon refresh (I think that seeing the perpetual password reset page might
        // be confusing; this prevents that from happening).
        //
        // Also, setting switchClinicId to the default value of '' will prevent the user from being stuck in a clinic
        // switch state (i.e. showing the switch clinic dialog on every page load).
        partialize: (state) =>
          state.parsedToken?.password_needs_reset
            ? { ...state, token: null, parsedToken: null, switchClinicId: '' }
            : state
      }
    )
  )
);

// Some facade functions to make it easier to use the Zustand store and to make it easier to migrate to another state
// management library (if needed).
export const useAuthToken = () => useAuthStore((state) => state.token);
export const useEmail = () => useAuthStore((state) => state.email);
export const useUserId = () => useAuthStore((state) => state.parsedToken?.user_id);
export const useClinicId = () => useAuthStore((state) => state.parsedToken?.clinic_id);
export const usePermissions = () => useAuthStore((state) => state.parsedToken?.permissions || []);

// useHasPermission returns whether the logged-in user holds the given permission. This is the preferred way to gate UI
// on what a user can do (as opposed to which role they hold).
export const useHasPermission = (permission: PermissionType) =>
  useAuthStore((state) => (state.parsedToken?.permissions || []).includes(permission));
export const useIsLoggedIn = () => useAuthStore((state) => !!state.token);
export const usePasswordNeedsReset = () => useAuthStore((state) => state.parsedToken?.password_needs_reset || false);
export const useStateLogout = () => useAuthStore((state) => state.logout);
export const useSetToken = () => useAuthStore((state) => state.setToken);
export const useSetEmail = () => useAuthStore((state) => state.setEmail);

// useSetParsedToken sets the parsed token in the store.  This is mostly used for testing.
export const useSetParsedToken = () => useAuthStore((state) => state.setParsedToken);

// These hooks handle the global clinic switching state
export const useSwitchClinicId = () => useAuthStore((state) => state.switchClinicId);
export const useResetSwitchClinicId = () => useAuthStore((state) => () => state.setSwitchClinicId(''));
